import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { Spinner, Stack } from '@agentisai/design-system';
import { useAuth } from '../auth/AuthProvider';

/**
 * RequireAuth — UX route guard (NOT the authorization boundary; the server
 * enforces access per frontend-architecture R13). While the SDK rehydrates
 * the session, show a Spinner. If unauthenticated, redirect to /login with a
 * ?next= return-URL (the current path+search, encoded) so post-login lands
 * back here. The ?next value survives the Auth0 round-trip via appState.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Stack as="main" align="center" justify="center" style={{ minHeight: '60vh' }}>
        <Spinner size="lg" tone="primary" label="Checking your session…" />
      </Stack>
    );
  }

  if (!isAuthenticated) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  return <>{children}</>;
}
RequireAuth.displayName = 'RequireAuth';
